import React, { useState, useEffect, useCallback } from "react";
import { X, Trash2, RefreshCw, Recycle, AlertTriangle } from "lucide-react";
import api from "../utils/api";
import { toast } from "react-toastify";
import { useAuth } from "./authentication/AuthContext";
import { formatIndianCurrency } from "../utils/Const";

const REASONS = [
  { id: "SPOILAGE",        label: "Spoilage" },
  { id: "PREP_WASTE",      label: "Prep waste" },
  { id: "CUSTOMER_RETURN", label: "Returned" },
  { id: "EXPIRED",         label: "Expired" },
  { id: "OTHER",           label: "Other" },
];

const REASON_LABEL = Object.fromEntries(REASONS.map(r => [r.id, r.label]));

const monthStart = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toLocaleDateString("en-CA");
};
const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });

const fmt = (n) => `₹${formatIndianCurrency(n || 0)}`;

export default function WastePanel({ onClose }) {
  const { isStaff, staffRole } = useAuth();
  const isManager = !isStaff || staffRole === "manager";

  const [range, setRange] = useState({ from: monthStart(), to: today() });
  const [report, setReport] = useState(null);
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ ingredientId: "", name: "", qty: "", reason: "SPOILAGE", note: "" });
  const [saving, setSaving] = useState(false);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/waste?from=${range.from}&to=${range.to}`);
      setReport(res.data);
    } catch {
      toast.error("Failed to load waste report");
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  useEffect(() => {
    // inventory list is manager-only on the server; fall back to free-text for cashiers
    api.get("/inventory").then(res => {
      setIngredients(res.data?.data || res.data?.inventory || res.data || []);
    }).catch(() => {});
  }, []);

  const selectedIngredient = ingredients.find?.(i => i._id === form.ingredientId);
  const estimatedCost = selectedIngredient && form.qty
    ? Number(form.qty) * (selectedIngredient.costPerUnit || 0)
    : null;

  const submit = async () => {
    if (!form.qty || Number(form.qty) <= 0) { toast.error("Enter quantity"); return; }
    if (!form.ingredientId && !form.name.trim()) { toast.error("Pick an ingredient or type an item name"); return; }
    setSaving(true);
    try {
      await api.post("/waste", {
        ingredientId: form.ingredientId || null,
        name: form.name,
        qty: Number(form.qty),
        reason: form.reason,
        note: form.note,
      });
      toast.success("Waste logged" + (form.ingredientId ? " — stock deducted" : ""));
      setForm({ ingredientId: "", name: "", qty: "", reason: "SPOILAGE", note: "" });
      fetchReport();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to log waste");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/waste/${id}`);
      toast.success("Entry deleted, stock restored");
      fetchReport();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete");
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-red-500/15 border border-red-500/25 flex items-center justify-center">
              <Recycle size={15} className="text-red-500" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-slate-800">Waste & Spoilage</h2>
              <p className="text-[10px] text-slate-400">Track kitchen waste and its cost impact</p>
            </div>
          </div>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400">
            <X size={14} />
          </button>
        </div>

        {/* Entry form */}
        <div className="border-b border-slate-100 px-5 py-3 space-y-2 bg-red-50/30">
          <div className="grid grid-cols-2 gap-2">
            <select value={form.ingredientId}
              onChange={e => setForm(f => ({ ...f, ingredientId: e.target.value, name: "" }))}
              className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-red-400">
              <option value="">— Inventory ingredient (auto-deduct) —</option>
              {(Array.isArray(ingredients) ? ingredients : []).map(i => (
                <option key={i._id} value={i._id}>{i.name} ({i.currentStock} {i.unit} left)</option>
              ))}
            </select>
            <input placeholder="…or type item (e.g. Butter Chicken plate)" value={form.name} disabled={!!form.ingredientId}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-red-400 disabled:opacity-40" />
          </div>
          <div className="flex gap-2">
            <input type="number" min="0" placeholder={`Qty${selectedIngredient ? ` (${selectedIngredient.unit})` : ""}`} value={form.qty}
              onChange={e => setForm(f => ({ ...f, qty: e.target.value }))}
              className="w-24 border border-slate-200 rounded-lg px-2 py-1.5 text-xs text-right focus:outline-none focus:ring-1 focus:ring-red-400" />
            <select value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
              className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-red-400">
              {REASONS.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
            <input placeholder="Note (optional)" value={form.note}
              onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
              onKeyDown={e => e.key === "Enter" && submit()}
              className="flex-1 border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-red-400" />
            <button onClick={submit} disabled={saving}
              className="px-4 py-1.5 text-xs font-bold bg-red-500 hover:bg-red-600 text-white rounded-lg disabled:opacity-50 shrink-0">
              Log
            </button>
          </div>
          {estimatedCost !== null && (
            <p className="text-[10px] text-slate-500">Estimated cost impact: <strong className="text-red-600">{fmt(estimatedCost)}</strong> (auto-calculated from ingredient cost)</p>
          )}
        </div>

        {/* Report */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
          <div className="flex items-center gap-2">
            <input type="date" value={range.from} onChange={e => setRange(r => ({ ...r, from: e.target.value }))}
              className="border border-slate-200 rounded-lg px-2 py-1 text-xs" />
            <span className="text-xs text-slate-400">→</span>
            <input type="date" value={range.to} onChange={e => setRange(r => ({ ...r, to: e.target.value }))}
              className="border border-slate-200 rounded-lg px-2 py-1 text-xs" />
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw size={16} className="animate-spin text-slate-400" />
            </div>
          ) : report && (
            <>
              {/* Summary */}
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-xl border border-red-200 bg-red-50/50 px-3 py-2.5">
                  <p className="text-[9px] font-black uppercase tracking-wider text-slate-400">Total waste cost</p>
                  <p className="text-sm font-black text-red-600 mt-0.5">{fmt(report.totalCost)}</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50/60 px-3 py-2.5">
                  <p className="text-[9px] font-black uppercase tracking-wider text-slate-400">Entries</p>
                  <p className="text-sm font-black text-slate-800 mt-0.5">{report.logs.length}</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50/60 px-3 py-2.5">
                  <p className="text-[9px] font-black uppercase tracking-wider text-slate-400">% of purchases</p>
                  <p className={`text-sm font-black mt-0.5 ${report.wastePercentOfPurchases > 5 ? "text-red-600" : "text-slate-800"}`}>
                    {report.wastePercentOfPurchases !== null ? `${report.wastePercentOfPurchases}%` : "—"}
                  </p>
                </div>
              </div>

              {report.wastePercentOfPurchases > 5 && (
                <div className="flex items-center gap-2 rounded-xl bg-amber-50 border border-amber-100 px-3 py-2">
                  <AlertTriangle size={13} className="text-amber-500 shrink-0" />
                  <p className="text-[11px] text-amber-700 font-semibold">Waste is above 5% of purchases — review storage and prep portions.</p>
                </div>
              )}

              {/* By reason */}
              {Object.keys(report.byReason).length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(report.byReason).map(([reason, data]) => (
                    <span key={reason} className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">
                      {REASON_LABEL[reason] || reason}: {fmt(data.cost)} ({data.count})
                    </span>
                  ))}
                </div>
              )}

              {/* Entries */}
              {report.logs.length === 0 ? (
                <p className="text-center text-xs text-slate-400 py-6">No waste logged in this period 🎉</p>
              ) : (
                <div className="space-y-1.5">
                  {report.logs.map(l => (
                    <div key={l._id} className="flex items-center gap-2 rounded-xl border border-slate-100 px-3 py-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold text-slate-700 truncate">
                          {l.name} <span className="font-normal text-slate-400">— {l.qty}{l.unit ? ` ${l.unit}` : ""}</span>
                        </p>
                        <p className="text-[10px] text-slate-400">
                          {l.date} · {REASON_LABEL[l.reason] || l.reason} · {l.loggedBy}{l.note ? ` · ${l.note}` : ""}
                        </p>
                      </div>
                      <span className="text-xs font-black text-red-600 shrink-0">{fmt(l.costImpact)}</span>
                      {isManager && (
                        <button onClick={() => remove(l._id)} title="Delete & restore stock"
                          className="w-6 h-6 flex items-center justify-center rounded-lg bg-slate-50 text-slate-400 hover:bg-rose-50 hover:text-rose-500 shrink-0">
                          <Trash2 size={11} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between gap-2">
          <p className="text-[10px] text-slate-400">
            Ingredient waste deducts stock automatically and calculates cost from the ingredient's cost per unit.
          </p>
          <button onClick={onClose} className="px-4 py-2 text-xs font-semibold bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200 shrink-0">Close</button>
        </div>
      </div>
    </div>
  );
}
